import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { useGetIdentity, useLogout, useTranslation } from "@refinedev/core";
import type { IResourceItem } from "@refinedev/core";
import { RefineThemedLayoutHeaderProps as RefineThemedLayoutV2HeaderProps } from "@refinedev/mui";

import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { Icon } from "@/components/engage";
import { BROADCAST_PERMISSION } from "@/config/permission-keys";
import { OrganizationPractitionersProvider, useMessengerContext } from "@/contexts";
import { ColorModeContext } from "@/contexts";
import { useFeaturesV2 } from "@/hooks/useFeaturesV2";
import { MessageBroadcastModal } from "@/routes/profile/components";
import { AppLogo } from "@/styles";
import { IUser } from "@/types";
import { getNameInitials } from "@/utils";
import { hasRequiredPermissions } from "@/utils/required-permissions";

import { logoutService } from "@ethora/chat-component";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { faCircleInfo, faUser, faUsersGear } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";

import { LanguageSwitcher } from "./LanguageSwitcher";

export const Header: React.FC<RefineThemedLayoutV2HeaderProps & { resources: IResourceItem[] }> = ({
  sticky = true,
  resources,
}) => {
  const { mode, setMode } = useContext(ColorModeContext);
  const { data: user } = useGetIdentity<IUser>();
  const { mutate: logout } = useLogout();
  const { translate } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Org licence gate (VMD-793): the Broadcast button and its modal exist only when the org has messenger
  const { enabled: messengerEnabled } = useMessengerContext();
  // Permission gates (VMD-796): featuresv2 keys, fail closed while loading so nothing admin-only flashes in
  const { hasFeature } = useFeaturesV2();
  const canBroadcast = messengerEnabled && hasFeature(BROADCAST_PERMISSION);
  const canManageOrganization = hasRequiredPermissions(
    resources.find(resource => resource.name === "organization-settings"),
    hasFeature
  );
  const theme = useTheme();
  // Below 1100px the toolbar no longer has room for the full-text broadcast button (VMD-602)
  const isCompactToolbar = useMediaQuery(theme.breakpoints.down(1100));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [isBroadcastMessageModalOpen, setIsBroadcastMessageModalOpen] = useState<boolean>(false);

  // The modal unmounts with the button; forget "open" too, or it reopens when the permission returns
  useEffect(() => {
    if (!canBroadcast) {
      setIsBroadcastMessageModalOpen(false);
    }
  }, [canBroadcast]);

  // Filter resources based on the user's permissions AND navigation presence
  // Only show resources that have a 'list' property (main navigation items)
  const visibleResources = resources.filter(resource => {
    // Must have a list route to appear in main navigation
    if (!resource.list) {
      return false;
    }

    // Any-of over meta.requiredPermissions; an unrestricted resource always shows
    return hasRequiredPermissions(resource, hasFeature);
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const handleAboutClick = () => {
    handleMenuClose();
    navigate("/profile/about");
  };

  const handleSignOut = () => {
    handleMenuClose();
    logout();
    try {
      logoutService.performLogout();
    } catch (err) {
      Sentry.captureMessage("Messenger: Unable to logout", {
        level: "error",
        extra: { component: "components/layout/header/index.tsx", error: err },
      });
    }
  };

  const handleOrganizationSettingsClick = () => {
    handleMenuClose();
    navigate("/profile/organization-settings");
  };

  const handleOpenBroadcastMessageModal = () => {
    setIsBroadcastMessageModalOpen(true);
  };

  const handleCloseBroadcastMessageModal = () => {
    setIsBroadcastMessageModalOpen(false);
  };

  return (
    <AppBar
      position={sticky ? "sticky" : "relative"}
      sx={{ backgroundColor: "primary.dark", height: 60, minWidth: 900 }}
    >
      <Toolbar>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2}>
            <AppLogo aria-labelledby={"Medly-logo"} />
            {visibleResources.map(resource => {
              const isActive =
                location.pathname === `/${resource.name}` ||
                (location.pathname === "/" && resource.name === resources[0].name);

              return (
                <Button
                  key={resource.name}
                  color="inherit"
                  component={Link}
                  to={`${resource.list}`}
                  sx={{
                    backgroundColor: isActive ? "primary.light" : "inherit",
                    color: "primary.contrastText",
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minHeight: 44,
                    "&:hover": {
                      backgroundColor: "primary.light",
                      opacity: 0.9,
                    },
                  }}
                >
                  <Typography variant="h6">
                    {resource?.meta?.label ? translate(resource.meta.label as string) : resource.name}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
            <LanguageSwitcher compact={isCompactToolbar} />

            <IconButton
              color="inherit"
              onClick={() => {
                setMode();
              }}
              // code has been laid out to implement the color mode context but differed for the initial version of one dashboard
              sx={{
                display: "none",
              }}
            >
              {mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton>

            {canBroadcast &&
              (isCompactToolbar ? (
                <Tooltip title={translate("buttons.broadcastMessage")}>
                  <IconButton
                    onClick={handleOpenBroadcastMessageModal}
                    aria-label={translate("buttons.broadcastMessage")}
                    sx={{
                      border: `1px solid ${theme.palette.primary.contrastText}`,
                      backgroundColor: "primary.dark",
                      // Match the account avatar sitting beside it
                      width: 35,
                      height: 35,
                      "&:hover": {
                        backgroundColor: "primary.main",
                      },
                    }}
                  >
                    <Icon name={faBullhorn} color={"primary.contrastText"} size={"xs"} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  onClick={handleOpenBroadcastMessageModal}
                  startIcon={<Icon name={faBullhorn} color={"primary.contrastText"} size={"xs"} />}
                  sx={{
                    border: `1px solid ${theme.palette.primary.contrastText}`,
                    backgroundColor: "primary.dark",
                    px: 1.25,
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: "primary.main",
                    },
                  }}
                  variant="contained"
                >
                  {translate("buttons.broadcastMessage")}
                </Button>
              ))}

            <Button
              onClick={handleMenuClick}
              sx={{
                color: "primary.contrastText",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
              endIcon={<KeyboardArrowDownIcon />}
            >
              <Avatar
                sx={{
                  backgroundColor: "primary.light",
                  width: 35,
                  height: 35,
                  mr: 1,
                }}
                alt={user?.name}
              >
                <Typography variant="body1" color="primary.contrastText">
                  {getNameInitials(user?.name ?? "")}
                </Typography>
              </Avatar>
              <Typography variant="body1">{user?.name ?? ""}</Typography>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 180,
                    mt: 1,
                  },
                },
              }}
            >
              <MenuItem onClick={handleProfileClick}>
                <Box component="span" mr={1} display="inline-flex">
                  <FontAwesomeIcon icon={faUser} />
                </Box>
                <Typography variant="body1">{translate("buttons.userProfile")}</Typography>
              </MenuItem>
              {canManageOrganization && (
                <MenuItem onClick={handleOrganizationSettingsClick}>
                  <FontAwesomeIcon icon={faUsersGear} style={{ marginRight: 8 }} />
                  <Typography variant="body1">{translate("buttons.organizationSettings")}</Typography>
                </MenuItem>
              )}
              <MenuItem onClick={handleAboutClick}>
                <Box component="span" mr={1} display="inline-flex">
                  <FontAwesomeIcon icon={faCircleInfo} />
                </Box>
                <Typography variant="body1">{translate("buttons.about")}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSignOut}>
                <Typography variant="body1">{translate("buttons.logout")}</Typography>
              </MenuItem>
            </Menu>

            {/* Mounted with the button: the provider runs org / practitioner / clinic reads whether or not the modal opens */}
            {canBroadcast && (
              <OrganizationPractitionersProvider>
                <MessageBroadcastModal open={isBroadcastMessageModalOpen} onClose={handleCloseBroadcastMessageModal} />
              </OrganizationPractitionersProvider>
            )}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
